/**
 * ErrorLogger - Utility class for consistent error formatting and logging
 * Provides standardized error logging with environment context, sanitization,
 * and debugging information for the conversation logging system.
 */

import EnvironmentDetector from './EnvironmentDetector.js';

class ErrorLogger {
  /**
   * Logs an error with comprehensive context and sanitization
   * @param {string} context - The context where the error occurred
   * @param {Error|string} error - The error object or message
   * @param {Object} metadata - Additional metadata to include
   * @param {string} level - Log level: 'error', 'warn', 'info'
   */
  static logError(context, error, metadata = {}, level = 'error') {
    const errorInfo = this.formatError(context, error, metadata);
    
    // Use appropriate console method based on level
    switch (level) {
      case 'warn':
        console.warn('ConversationLogger Warning:', errorInfo);
        break;
      case 'info':
        console.info('ConversationLogger Info:', errorInfo);
        break;
      case 'error':
      default:
        console.error('ConversationLogger Error:', errorInfo);
        break;
    }

    // In production, could also send to external error tracking service
    if (this.shouldReportToExternalService(level)) {
      this.reportToExternalService(errorInfo, level);
    }

    return errorInfo;
  }

  /**
   * Formats error information with comprehensive context
   * @param {string} context - The context where the error occurred
   * @param {Error|string} error - The error object or message
   * @param {Object} metadata - Additional metadata to include
   * @returns {Object} - Formatted error information
   */
  static formatError(context, error, metadata = {}) {
    const timestamp = new Date().toISOString();
    const environment = this.getEnvironmentSafely();
    
    // Extract error information safely
    const errorInfo = this.extractErrorInfo(error);
    
    // Sanitize metadata to prevent logging sensitive data
    const sanitizedMetadata = this.sanitizeMetadata(metadata);
    
    return {
      timestamp,
      context,
      environment,
      error: errorInfo,
      metadata: sanitizedMetadata,
      system: {
        userAgent: this.getUserAgentSafely(),
        url: this.getUrlSafely(),
        environmentDetection: this.getEnvironmentDetectionSafely()
      }
    };
  }

  /**
   * Extracts error information safely from various error types
   * @param {Error|string|any} error - The error to extract information from
   * @returns {Object} - Extracted error information
   */
  static extractErrorInfo(error) {
    if (!error) {
      return { message: 'Unknown error', type: 'unknown' };
    }

    if (typeof error === 'string') {
      return { message: error, type: 'string' };
    }

    if (error instanceof Error) {
      return {
        message: error.message,
        name: error.name,
        stack: error.stack,
        type: 'Error'
      };
    }

    // Handle other types of error objects
    if (typeof error === 'object') {
      return {
        message: error.message || error.toString(),
        name: error.name || 'UnknownError',
        stack: error.stack || null,
        type: 'object',
        raw: this.sanitizeObject(error)
      };
    }

    return {
      message: String(error),
      type: typeof error
    };
  }

  /**
   * Sanitizes metadata to prevent logging sensitive information
   * @param {Object} metadata - Metadata to sanitize
   * @returns {Object} - Sanitized metadata
   */
  static sanitizeMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object') {
      return {};
    }

    const sanitized = {};
    const sensitiveKeys = [
      'password', 'token', 'key', 'secret', 'auth', 'authorization',
      'cookie', 'session', 'jwt', 'api_key', 'apikey', 'access_token',
      'refresh_token', 'private_key', 'client_secret'
    ];

    for (const [key, value] of Object.entries(metadata)) {
      const lowerKey = key.toLowerCase();
      
      // Check if key contains sensitive information
      if (sensitiveKeys.some(sensitiveKey => lowerKey.includes(sensitiveKey))) {
        sanitized[key] = '[REDACTED]';
        continue;
      }

      // Recursively sanitize nested objects
      if (value && typeof value === 'object') {
        sanitized[key] = this.sanitizeObject(value);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Sanitizes an object recursively
   * @param {Object} obj - Object to sanitize
   * @param {number} depth - Current recursion depth (to prevent infinite loops)
   * @returns {Object} - Sanitized object
   */
  static sanitizeObject(obj, depth = 0) {
    // Prevent infinite recursion
    if (depth > 3) {
      return '[MAX_DEPTH_REACHED]';
    }

    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item, depth + 1));
    }

    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeObject(value, depth + 1);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Safely gets environment information
   * @returns {string} - Environment string
   */
  static getEnvironmentSafely() {
    try {
      return EnvironmentDetector.detectEnvironment();
    } catch (error) {
      return 'unknown';
    }
  }

  /**
   * Safely gets environment detection metadata
   * @returns {Object|null} - Environment detection metadata
   */
  static getEnvironmentDetectionSafely() {
    try {
      return EnvironmentDetector.getDetectionMetadata();
    } catch (error) {
      return null;
    }
  }

  /**
   * Safely gets user agent information
   * @returns {string|null} - User agent string
   */
  static getUserAgentSafely() {
    try {
      return typeof navigator !== 'undefined' ? navigator.userAgent : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Safely gets current URL information
   * @returns {string|null} - Current URL
   */
  static getUrlSafely() {
    try {
      return typeof window !== 'undefined' && window.location ? window.location.href : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Determines if errors should be reported to external service
   * @param {string} level - Log level
   * @returns {boolean} - Whether to report to external service
   */
  static shouldReportToExternalService(level) {
    // Only report errors in production, not warnings or info
    return level === 'error' && this.getEnvironmentSafely() === 'production';
  }

  /**
   * Reports error to external service (placeholder for future implementation)
   * @param {Object} errorInfo - Formatted error information
   * @param {string} level - Log level
   */
  static reportToExternalService(errorInfo, level) {
    // Placeholder for external error reporting service integration
    // Could integrate with services like Sentry, LogRocket, etc.
    console.info('External error reporting not configured:', { errorInfo, level });
  }

  /**
   * Creates a standardized error context object
   * @param {string} operation - The operation being performed
   * @param {Object} additionalContext - Additional context information
   * @returns {Object} - Standardized error context
   */
  static createContext(operation, additionalContext = {}) {
    return {
      operation,
      timestamp: new Date().toISOString(),
      environment: this.getEnvironmentSafely(),
      ...additionalContext
    };
  }

  /**
   * Logs validation errors with specific formatting
   * @param {string} context - Context where validation failed
   * @param {Array} validationErrors - Array of validation error messages
   * @param {Object} data - The data that failed validation (will be sanitized)
   */
  static logValidationErrors(context, validationErrors, data = {}) {
    const metadata = {
      validationErrors,
      invalidData: this.sanitizeMetadata(data),
      errorCount: validationErrors.length
    };

    this.logError(context, 'Validation failed', metadata, 'warn');
  }

  /**
   * Logs retry attempts with context
   * @param {string} context - Context of the retry
   * @param {number} attempt - Current attempt number
   * @param {number} maxAttempts - Maximum number of attempts
   * @param {Error} error - The error that caused the retry
   * @param {number} retryDelay - Delay before next retry
   */
  static logRetryAttempt(context, attempt, maxAttempts, error, retryDelay) {
    const metadata = {
      attempt,
      maxAttempts,
      retryDelay,
      isLastAttempt: attempt >= maxAttempts
    };

    const level = attempt >= maxAttempts ? 'error' : 'warn';
    this.logError(context, error, metadata, level);
  }
}

export default ErrorLogger;